import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './ApplyPage.css';

const ApplyPage = () => {
  const { slug } = useParams(); // Changed from id to slug
  const navigate = useNavigate();
  
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    role: '',
    linkedin: '',
    github: '',
    resume: '',
    portfolio: '',
    message: '',
    expectedSalary: '',
    noticePeriod: '',
    howDidYouHear: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [errors, setErrors] = useState({});

  // Jobs data (same as CareerPage)
  const jobs = [
    {
      id: 1,
      title: 'Frontend Developer (Fresher)',
      slug: 'frontend-developer-fresher',
      department: 'Engineering',
      location: 'Remote',
      type: 'Full-time',
      experience: '0-1 years',
      salary: '2-4 LPA',
      description: 'Looking for passionate freshers with knowledge of React and modern web technologies.',
      requirements: [
        'Basic knowledge of React.js',
        'HTML, CSS, JavaScript',
        'Eagerness to learn',
        'Good problem-solving skills'
      ],
      posted: 'Recently',
      icon: '⚛️'
    },
    {
      id: 2,
      title: 'Backend Developer Intern',
      slug: 'backend-developer-intern',
      department: 'Engineering',
      location: 'Remote',
      type: 'Internship',
      experience: '0-1 years',
      salary: 'Stipend Based',
      description: 'Internship opportunity for backend development using Node.js / Python.',
      requirements: [
        'Basic Node.js or Python',
        'Understanding of APIs',
        'Database basics',
        'Willing to learn'
      ],
      posted: 'Recently',
      icon: '⚙️'
    },
    {
      id: 3,
      title: 'Business Development Executive (BDE)',
      slug: 'business-development-executive',
      department: 'Sales',
      location: 'Remote',
      type: 'Full-time',
      experience: '0-2 years',
      salary: '3-6 LPA + Incentives',
      description: 'We are hiring BDEs to generate leads, connect with clients, and grow our business.',
      requirements: [
        'Good communication skills',
        'Client handling',
        'Basic sales knowledge',
        'Confident personality'
      ],
      posted: 'Recently',
      icon: '💼'
    },
    {
      id: 4,
      title: 'HR Executive',
      slug: 'hr-executive',
      department: 'HR',
      location: 'Remote',
      type: 'Full-time',
      experience: '0-2 years',
      salary: '2-5 LPA',
      description: 'Looking for HR to manage hiring, employee engagement, and company culture.',
      requirements: [
        'Good communication',
        'Basic HR knowledge',
        'Recruitment process understanding',
        'People management skills'
      ],
      posted: 'Recently',
      icon: '🤝'
    },
    {
      id: 5,
      title: 'Digital Marketing Intern',
      slug: 'digital-marketing-intern',
      department: 'Marketing',
      location: 'Remote',
      type: 'Internship',
      experience: '0-1 years',
      salary: 'Stipend Based',
      description: 'Work on social media, SEO, and digital campaigns.',
      requirements: [
        'Basic social media knowledge',
        'Content creation',
        'SEO basics',
        'Creative mindset'
      ],
      posted: 'Recently',
      icon: '📈'
    }
  ];

  useEffect(() => {
    // Find the job based on the slug parameter
    const decodedSlug = decodeURIComponent(slug);
    const foundJob = jobs.find(job => job.slug === decodedSlug);
    
    if (foundJob) {
      setJob(foundJob);
      setFormData(prev => ({
        ...prev,
        role: foundJob.title
      }));
    } else {
      // Handle invalid job slug
      navigate('/careers');
    }
    
    setLoading(false);
  }, [slug, navigate]);

  const validateUrl = (url, platform) => {
    if (!url) return `${platform} profile link is required`;
    
    const linkedinPattern = /^https?:\/\/(www\.)?linkedin\.com\/in\/[\w-]+\/?$/;
    const githubPattern = /^https?:\/\/(www\.)?github\.com\/[\w-]+\/?$/;
    
    if (platform === 'LinkedIn' && !linkedinPattern.test(url)) {
      return 'Please enter a valid LinkedIn profile URL (e.g., https://www.linkedin.com/in/username)';
    }
    if (platform === 'GitHub' && !githubPattern.test(url)) {
      return 'Please enter a valid GitHub profile URL (e.g., https://github.com/username)';
    }
    return '';
  };

  const validateEmail = (email) => {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) return 'Email is required';
    if (!emailPattern.test(email)) return 'Please enter a valid email address';
    return '';
  };

  const validatePhone = (phone) => {
    const phonePattern = /^[\+]?[(]?[0-9]{1,4}[)]?[-\s\.]?[(]?[0-9]{1,4}[)]?[-\s\.]?[0-9]{3,4}[-\s\.]?[0-9]{3,4}$/;
    if (!phone) return 'Phone number is required';
    if (!phonePattern.test(phone)) return 'Please enter a valid phone number';
    return '';
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({...formData, [name]: value});
    
    if (errors[name]) {
      setErrors({...errors, [name]: ''});
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) newErrors.name = 'Full name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else {
      const emailError = validateEmail(formData.email);
      if (emailError) newErrors.email = emailError;
    }
    
    if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
    else {
      const phoneError = validatePhone(formData.phone);
      if (phoneError) newErrors.phone = phoneError;
    }
    
    if (!formData.linkedin.trim()) newErrors.linkedin = 'LinkedIn profile link is required';
    else {
      const linkedinError = validateUrl(formData.linkedin, 'LinkedIn');
      if (linkedinError) newErrors.linkedin = linkedinError;
    }
    
    if (!formData.github.trim()) newErrors.github = 'GitHub profile link is required';
    else {
      const githubError = validateUrl(formData.github, 'GitHub');
      if (githubError) newErrors.github = githubError;
    }
    
    if (!formData.resume.trim()) newErrors.resume = 'Resume link is required';
    else if (!formData.resume.match(/^https?:\/\/.+/)) {
      newErrors.resume = 'Please enter a valid URL starting with http:// or https://';
    }
    
    if (!formData.message.trim()) newErrors.message = 'Please tell us why we should hire you';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      const firstError = document.querySelector('.error-message');
      if (firstError) {
        firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }
    
    setIsSubmitting(true);
    
    setTimeout(() => {
      const applicationData = {
        ...formData,
        jobId: job.id,
        jobTitle: job.title,
        department: job.department,
        appliedDate: new Date().toISOString()
      };
      
      console.log('Application Submitted:', applicationData);
      
      setSubmitStatus('success');
      setIsSubmitting(false);
      
      setTimeout(() => {
        navigate('/careers');
      }, 2000);
    }, 1500);
  };

  if (loading) {
    return (
      <div className="apply-page">
        <div className="apply-container">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Loading job details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="apply-page">
        <div className="apply-container">
          <div className="error-container">
            <h2>Job Not Found</h2>
            <p>The position you're looking for doesn't exist.</p>
            <button onClick={() => navigate('/careers')} className="back-btn">
              View All Jobs
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="apply-page">
      <div className="apply-container">
        <div className="apply-header">
          <div className="job-icon">{job.icon}</div>
          <h1>Apply for {job.title}</h1>
          <div className="job-badges">
            <span className="badge">{job.department}</span>
            <span className="badge">{job.type}</span>
            <span className="badge">{job.location}</span>
            <span className="badge">{job.experience}</span>
          </div>
        </div>

        <div className="job-summary">
          <h3>Position Overview</h3>
          <p>{job.description}</p>
          <div className="salary-info">
            <strong>💰 Salary Range:</strong> {job.salary}
          </div>
          <details className="requirements-details">
            <summary>📋 Key Requirements</summary>
            <ul>
              {job.requirements.map((req, index) => (
                <li key={index}>{req}</li>
              ))}
            </ul>
          </details>
        </div>

        {submitStatus === 'success' && (
          <div className="success-message">
            <h3>✓ Application Submitted Successfully!</h3>
            <p>Thank you for applying to the <strong>{job.title}</strong> position at Zuztan Tech. Our HR team will review your application and get back to you within 3-5 business days.</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="apply-form">
          <div className="form-section">
            <h3>Personal Information</h3>
            
            <div className="form-group">
              <label htmlFor="name">Full Name *</label>
              <input 
                type="text" 
                id="name"
                name="name" 
                placeholder="Enter your full name" 
                value={formData.name}
                onChange={handleChange} 
                required 
                disabled={isSubmitting}
                className={errors.name ? 'error' : ''}
              />
              {errors.name && <div className="error-message">{errors.name}</div>}
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="email">Email Address *</label>
                <input 
                  type="email" 
                  id="email"
                  name="email" 
                  placeholder="your.email@example.com" 
                  value={formData.email}
                  onChange={handleChange} 
                  required 
                  disabled={isSubmitting}
                  className={errors.email ? 'error' : ''}
                />
                {errors.email && <div className="error-message">{errors.email}</div>}
              </div>

              <div className="form-group">
                <label htmlFor="phone">Phone Number *</label>
                <input 
                  type="tel" 
                  id="phone"
                  name="phone" 
                  placeholder="+1 234 567 8900" 
                  value={formData.phone}
                  onChange={handleChange} 
                  required 
                  disabled={isSubmitting}
                  className={errors.phone ? 'error' : ''}
                />
                {errors.phone && <div className="error-message">{errors.phone}</div>}
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>Professional Profiles</h3>
            
            <div className="form-group">
              <label htmlFor="linkedin">LinkedIn Profile *</label>
              <input 
                type="url" 
                id="linkedin"
                name="linkedin" 
                placeholder="https://www.linkedin.com/in/your-profile" 
                value={formData.linkedin}
                onChange={handleChange} 
                required 
                disabled={isSubmitting}
                className={errors.linkedin ? 'error' : ''}
              />
              <small>Provide your LinkedIn profile URL (e.g., https://www.linkedin.com/in/username)</small>
              {errors.linkedin && <div className="error-message">{errors.linkedin}</div>}
            </div>

            <div className="form-group">
              <label htmlFor="github">GitHub Profile *</label>
              <input 
                type="url" 
                id="github"
                name="github" 
                placeholder="https://github.com/your-username" 
                value={formData.github}
                onChange={handleChange} 
                required 
                disabled={isSubmitting}
                className={errors.github ? 'error' : ''}
              />
              <small>Provide your GitHub profile URL (e.g., https://github.com/username)</small>
              {errors.github && <div className="error-message">{errors.github}</div>}
            </div>

            <div className="form-group">
              <label htmlFor="resume">Resume/CV Link *</label>
              <input 
                type="url" 
                id="resume"
                name="resume" 
                placeholder="https://drive.google.com/your-resume" 
                value={formData.resume}
                onChange={handleChange} 
                required 
                disabled={isSubmitting}
                className={errors.resume ? 'error' : ''}
              />
              <small>Please provide a Google Drive, Dropbox, or any cloud storage link to your resume</small>
              {errors.resume && <div className="error-message">{errors.resume}</div>}
            </div>

            <div className="form-group">
              <label htmlFor="portfolio">Portfolio/Website (Optional)</label>
              <input 
                type="url" 
                id="portfolio"
                name="portfolio" 
                placeholder="https://your-portfolio.com" 
                value={formData.portfolio}
                onChange={handleChange} 
                disabled={isSubmitting}
              />
              <small>Share your portfolio, personal website, or any relevant work samples</small>
            </div>
          </div>

          <div className="form-section">
            <h3>Additional Information</h3>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="expectedSalary">Expected Salary (LPA)</label>
                <input 
                  type="text" 
                  id="expectedSalary"
                  name="expectedSalary" 
                  placeholder="e.g., 3.5 LPA" 
                  value={formData.expectedSalary}
                  onChange={handleChange} 
                  disabled={isSubmitting}
                />
              </div>

              <div className="form-group">
                <label htmlFor="noticePeriod">Notice Period</label>
                <input 
                  type="text" 
                  id="noticePeriod"
                  name="noticePeriod" 
                  placeholder="e.g., Immediate / 15 days / 1 month" 
                  value={formData.noticePeriod}
                  onChange={handleChange} 
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="howDidYouHear">How did you hear about this opportunity?</label>
              <select 
                id="howDidYouHear"
                name="howDidYouHear"
                value={formData.howDidYouHear}
                onChange={handleChange}
                disabled={isSubmitting}
              >
                <option value="">Select an option</option>
                <option value="LinkedIn">LinkedIn</option>
                <option value="Indeed">Indeed</option>
                <option value="Naukri">Naukri.com</option>
                <option value="Company Website">Company Website</option>
                <option value="Referral">Referral</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="message">Why should we hire you? *</label>
              <textarea 
                id="message"
                name="message" 
                placeholder="Tell us about your skills, experience, and what makes you the perfect candidate for this role..."
                rows="5"
                value={formData.message}
                onChange={handleChange} 
                required
                disabled={isSubmitting}
                className={errors.message ? 'error' : ''}
              ></textarea>
              {errors.message && <div className="error-message">{errors.message}</div>}
            </div>
          </div>

          <button 
            type="submit" 
            className="submit-btn"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Submitting Application...' : `Submit Application for ${job.title}`}
          </button>
        </form>

        <div className="hr-contact">
          <div className="hr-contact-content">
            <h3>📧 Connect with HR</h3>
            <p>For any questions about your application or the recruitment process, please reach out to our HR team:</p>
            <a href="mailto:info@zuztantech.com" className="hr-email">
              info@zuztantech.com
            </a>
            <p className="hr-note">Our HR team typically responds within 24-48 hours</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApplyPage;